import { useRef, type ComponentType, type ReactNode, type SVGProps } from 'react'

import { Dialog } from '~/components/dialog'
import { Tooltip } from '~/components/ui/tooltip'
import { ChevronLeftIcon, CloseIcon } from '~/components/icons'
import { Working } from '~/components/working'
import { useBeat, useWork, isWork } from '~/components/use-work'
import { cn } from '~/lib/cn'

/**
 * A page of the app opened over the page beneath it, per `/specs/app.spec.md`. Everything one
 * of these holds was written as a page and still reads as one, so the panel wears the page's
 * own colour rather than a raised surface — the cards and inputs inside it are then sitting on
 * exactly the ground they were drawn for, and none of them had to be restyled to come here.
 *
 * The heading is the panel's own, laid out the one way every page lays it out: the way out at
 * the near end, then the page's mark where it has one, then its name, and at the far end what
 * can be done to the page — each a glyph with its name for ears. A page reached from another
 * one inside the same dialog carries the way back where the way out would stand.
 *
 * Where the page owns its name, the name is edited where it stands: pressing it turns it into
 * a field of the same size in the same place, so nothing moves, and a press anywhere else, or
 * leaving the field, turns it back into text keeping what was typed.
 */
export function PageDialog({
  title,
  icon,
  onTitle,
  titleText,
  onTitleStart,
  onTitleDone,
  titlePlaceholder = 'Name',
  actions,
  titleBelow = false,
  foot,
  origin,
  flush = false,
  danger = false,
  onClose,
  onBack,
  className,
  fixedHeight = false,
  children,
}: {
  title: string
  /** The page's mark, beside its name. */
  icon?: ReactNode
  /** Where the page owns its name: typing into it, as it is edited where it stands. */
  onTitle?: ((next: string) => void) | undefined
  /** What the name's field holds, where that is not what the page is called — a page not yet named. */
  titleText?: string | undefined
  /** The name taken hold of, and let go of — for a page that counts a run of typing as one step. */
  onTitleStart?: (() => void) | undefined
  onTitleDone?: (() => void) | undefined
  /** What an empty name says, where the name can be edited. */
  titlePlaceholder?: string
  /** What can be done to the page, at the far end of its heading. */
  actions?: ReactNode
  /**
   * A page that names itself under the heading — its mark and its name as a block of its
   * own, the way a food's and a meal's do — says its name there and not in the heading too;
   * the heading still carries it for ears.
   */
  titleBelow?: boolean
  /**
   * A panel along the page's foot — the amount a food is logged in, the key that logs a meal.
   * It is part of the page's chrome rather than of what scrolls: pinned to the page's bottom
   * whatever is scrolled under it, the way the heading is pinned to its top.
   */
  foot?: ReactNode
  /** Where the page comes out of — see `Dialog`. */
  origin?: { x: number; y: number } | undefined
  /**
   * A page whose contents fill it edge to edge, a hair inside the panel — the camera's view,
   * which is a picture rather than words and wants the whole page. What is held is laid out
   * as a column it can stretch to the foot of.
   */
  flush?: boolean
  /** A page whose subject is losing something names itself in the danger ink. */
  danger?: boolean
  onClose: () => void
  /** Offered only where this page was reached from another one inside the same dialog. */
  onBack?: (() => void) | undefined
  /** Merged over the panel's own classes, for a page that wants a different width. */
  className?: string | undefined
  /**
   * A page that keeps one height whatever it holds — as tall as the window allows, up to a
   * ceiling — with what runs past it scrolling inside. For a page whose contents come and go,
   * a search answering above all, so that answering never moves the page under the pointer.
   */
  fixedHeight?: boolean
  children: ReactNode
}) {
  const field = useRef<HTMLInputElement>(null)

  const page = (
    <>
      {/*
        Pinned to the top of the backdrop, because these pages are as long as their contents:
        a list of every workout scrolls for a while, and its name and its way out must not be
        somewhere back up there. It fades out under itself rather than ending in an edge, the
        way the panel along a page's foot does, so what scrolls under it goes quietly.
      */}
      <header className="sticky top-0 z-10 flex items-center gap-2 rounded-t-3xl bg-surface px-4 py-3 after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-8 after:bg-gradient-to-b after:from-surface after:to-transparent after:content-['']">
        {onBack !== undefined ? (
          <button type="button" aria-label="Back" className="icon-btn -ml-1" onClick={onBack}>
            <ChevronLeftIcon aria-hidden className="size-7" />
          </button>
        ) : (
          <button type="button" aria-label="Close" className="icon-btn -ml-1" onClick={onClose}>
            <CloseIcon aria-hidden className="size-7" />
          </button>
        )}
        {icon !== undefined && <span className="shrink-0">{icon}</span>}
        {onTitle === undefined ? (
          <h1
            className={cn(
              'min-w-0 flex-1 truncate px-2 text-2xl font-medium',
              danger && 'text-danger',
              titleBelow && 'sr-only',
            )}
          >
            {title}
          </h1>
        ) : (
          /*
           * The name as text and as a field are one thing: a field that wears the name's own
           * type until it is pressed, when it takes the field's ground and ring in the same
           * box — so turning one into the other moves nothing on the page. Leaving it, or a
           * press anywhere else, takes the focus off it and turns it back.
           */
          <input
            ref={field}
            aria-label={titlePlaceholder}
            placeholder={titlePlaceholder}
            value={titleText ?? title}
            className={cn(
              'min-w-0 flex-1 rounded-lg bg-transparent px-2 py-0.5 text-2xl font-medium outline-none transition-colors',
              'hover:bg-hover focus:bg-surface-raised focus:ring-2 focus:ring-accent',
              danger && 'text-danger',
            )}
            onFocus={(event) => {
              event.target.select()
              onTitleStart?.()
            }}
            onChange={(event) => {
              onTitle(event.target.value)
            }}
            onBlur={() => {
              onTitleDone?.()
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === 'Escape') {
                event.currentTarget.blur()
              }
            }}
          />
        )}
        {titleBelow && <span className="min-w-0 flex-1" />}
        {actions !== undefined && (
          <div className="flex shrink-0 items-center gap-0.5">{actions}</div>
        )}
        {onBack !== undefined && (
          <button type="button" aria-label="Close" className="icon-btn -mr-1" onClick={onClose}>
            <CloseIcon aria-hidden className="size-7" />
          </button>
        )}
      </header>
      {/* Room under the heading's fade, so the first line of the page is not read through it. */}
      <div
        className={cn(
          flush ? 'flex flex-col p-2 pt-0' : 'px-6 pt-5 pb-6',
          (fixedHeight || foot !== undefined) && 'min-h-0 flex-1',
          (fixedHeight || foot !== undefined) && !flush && 'overflow-y-auto',
          foot !== undefined && 'pb-44',
        )}
      >
        {children}
      </div>
      {foot !== undefined && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 rounded-b-3xl bg-gradient-to-t from-surface via-surface/95 to-transparent px-6 pt-10 pb-5 [&>*]:pointer-events-auto">
          {foot}
        </div>
      )}
    </>
  )

  return (
    <Dialog
      label={title}
      onDismiss={onClose}
      origin={origin}
      className={cn(
        'relative max-w-2xl space-y-0 bg-surface p-0 shadow-2xl ring-1 ring-white/10',
        (fixedHeight || foot !== undefined) &&
          'flex h-[min(46rem,calc(100dvh-3rem))] flex-col overflow-hidden',
        className,
      )}
    >
      {page}
    </Dialog>
  )
}

/**
 * One of the things a page's heading offers: a glyph with its name under it — on the button for
 * ears at all times, and floating under it for eyes while the pointer rests there. It used to
 * grow the name out beside the glyph, which meant a heading changed width every time the
 * pointer crossed it.
 */
export function HeaderAction({
  label,
  Icon,
  pressed,
  danger = false,
  accent = false,
  disabled = false,
  onPress,
  working = false,
}: {
  label: string
  /** For an action that is a state — a favourite made or unmade. */
  pressed?: boolean | undefined
  Icon: ComponentType<SVGProps<SVGSVGElement>>
  danger?: boolean
  accent?: boolean
  disabled?: boolean
  /**
   * What pressing it does. Handing back a promise makes it work to wait on — the app spec, A
   * press that waits: the glyph gives way to a spinner and a second press does nothing until
   * it settles.
   */
  onPress: () => unknown
  /** Work under way this action is part of without having started it. */
  working?: boolean
}) {
  const work = useWork()
  const busy = work.working || working
  const shown = useBeat(busy)
  return (
    <Tooltip label={label}>
      <button
        type="button"
        aria-label={label}
        {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
        aria-busy={busy || undefined}
        disabled={disabled}
        className={cn(
          'relative flex items-center rounded-full p-2 transition-colors hover:bg-hover disabled:opacity-40',
          danger
            ? 'text-danger hover:bg-danger/10'
            : accent
              ? 'text-accent'
              : 'text-ink-muted hover:text-ink',
        )}
        onClick={() => {
          if (busy) {
            return
          }
          const done = onPress()
          if (isWork(done)) {
            void work.run(async () => await done).catch(() => undefined)
          }
        }}
      >
        <Working shown={shown} spinner="size-6">
          <Icon aria-hidden className="size-6 shrink-0" />
        </Working>
      </button>
    </Tooltip>
  )
}
